import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import AddAddressWhitelistDrawer from './components/AddAddressWhitelistDrawer';
import BatchDeleteWhitelistModal from './components/BatchDeleteWhitelistModal';
import {
  GET_ADDRESS_WHITELIST_LIST,
  DELETE_ADDRESS_WHITELIST,
  GetAddressWhitelistListResponse,
  AddressWhitelistType,
} from './graphql/addressWhitelistGraphql';

const AddressWhitelistPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [addDrawerKey, setAddDrawerKey] = useState(0);
  const [batchDeleteVisible, setBatchDeleteVisible] = useState(false);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressWhitelistListResponse>(
    GET_ADDRESS_WHITELIST_LIST,
    {
      variables: {
        originalZipcode: appliedFilters.originalZipcode || undefined,
        originalState: appliedFilters.originalState || undefined,
        originalCity: appliedFilters.originalCity || undefined,
        originalFullAddress: appliedFilters.originalFullAddress || undefined,
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteWhitelist] = useMutation(DELETE_ADDRESS_WHITELIST, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDeleteConfirm = (record: AddressWhitelistType) => {
    Modal.confirm({
      title: t('mapTool.addressWhitelist.deleteTitle') as string,
      content: t('mapTool.addressWhitelist.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteWhitelist({ variables: { id: Number(record.id) } }),
    });
  };

  const columns: TableColumnsType<AddressWhitelistType> = [
    {
      title: t('mapTool.addressWhitelist.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelist.table.zipcode'),
      dataIndex: 'originalZipcode',
      key: 'originalZipcode',
      width: 100,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelist.table.state'),
      dataIndex: 'originalState',
      key: 'originalState',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelist.table.city'),
      dataIndex: 'originalCity',
      key: 'originalCity',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressWhitelist.table.fullAddress'),
      dataIndex: 'originalFullAddress',
      key: 'originalFullAddress',
      width: 340,
      ellipsis: true,
      render: (v: string) => <span style={{ whiteSpace: 'pre' }}>{v}</span>,
    },
    {
      title: t('mapTool.addressWhitelist.table.standardizedFullAddress'),
      dataIndex: 'standardizedFullAddress',
      key: 'standardizedFullAddress',
      width: 340,
      ellipsis: true,
      render: (v: string | null | undefined) => <span style={{ whiteSpace: 'pre' }}>{v ?? '—'}</span>,
    },
    {
      title: t('mapTool.addressWhitelist.table.addressId'),
      dataIndex: 'addressId',
      key: 'addressId',
      width: 120,
      align: 'center',
      render: (v: number | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressWhitelist.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 160,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            size="small"
            danger
            onClick={() => showDeleteConfirm(record)}
          >
            {t('components.button.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressWhitelistList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressWhitelist.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => {
              setAddDrawerKey((k) => k + 1);
              setAddDrawerVisible(true);
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.new')}</span>
          </Button>
          <Button
            danger
            onClick={() => setBatchDeleteVisible(true)}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('mapTool.addressWhitelist.batchDelete.button')}</span>
          </Button>
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) =>
              t('mapTool.addressWhitelist.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <AddAddressWhitelistDrawer
        key={addDrawerKey}
        visible={addDrawerVisible}
        onClose={() => setAddDrawerVisible(false)}
        onSuccess={() => refetch()}
      />

      <BatchDeleteWhitelistModal
        visible={batchDeleteVisible}
        onClose={() => setBatchDeleteVisible(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default AddressWhitelistPage;
